/**
 * ContentGuard™ — Contents Management module.
 *
 * Five-phase wizard rendered as an inline expandable panel inside the
 * Active Jobs screen. Hard rules (panoramic lock, photo cap, etc.) are
 * enforced both on the server (HTTP 423) and surfaced in the UI.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { openCamera } from "@/lib/camera";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ShieldCheck, Camera, Box as BoxIcon, AlertTriangle, Lock, Unlock,
  CheckCircle2, ChevronRight, ChevronLeft, Plus, X, Sparkles, Loader2,
  FileText, Send, Star, Truck, Warehouse, ClipboardCheck, ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { AIResultNote } from "@/components/AIResultNote";

const AREA_PRESETS = [
  "Living Room", "Master Bedroom", "Bedroom 2", "Bedroom 3", "Kitchen",
  "Dining Room", "Bathroom", "Garage", "Basement", "Attic", "Hallway",
  "Office", "General/Miscellaneous",
];

const CATEGORIES = [
  "Furniture", "Electronics", "Appliances", "Clothing & Textiles",
  "Documents & Media", "Art & Collectibles", "Jewelry & Watches",
  "Tools & Equipment", "Sporting Goods", "Kitchenware", "Toys & Hobbies",
  "Medical Equipment", "Musical Instruments", "High-Value Item", "Other",
];
const HIGH_VALUE_CATS = new Set([
  "High-Value Item", "Jewelry & Watches", "Art & Collectibles", "Musical Instruments",
]);

const PANO_LABELS = [
  { id: "north",  prompt: "North wall / front of room — pan the full width" },
  { id: "south",  prompt: "South wall / back of room — pan the full width" },
  { id: "east",   prompt: "East wall — pan the full width" },
  { id: "west",   prompt: "West wall — pan the full width" },
  { id: "center", prompt: "Center of room — capture the full floor area" },
];

const CONDITIONS = [
  { id: "like_new",  label: "Like New",  color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { id: "good",      label: "Good",      color: "bg-blue-100 text-blue-700 border-blue-300" },
  { id: "fair",      label: "Fair",      color: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  { id: "damaged",   label: "Damaged",   color: "bg-orange-100 text-orange-700 border-orange-300" },
  { id: "destroyed", label: "Destroyed (Total Loss)", color: "bg-red-100 text-red-700 border-red-300" },
];
const SEVERITIES = [
  { id: "minor",      label: "Minor (cosmetic only)" },
  { id: "moderate",   label: "Moderate (functional impact)" },
  { id: "severe",     label: "Severe (major structural)" },
  { id: "total_loss", label: "Total Loss" },
];
const CLEANINGS = [
  "No Cleaning Needed", "Dry Cleaning", "Wet Cleaning", "Ultrasonic Cleaning",
  "Ozone Treatment", "Deodorization", "Specialty Cleaning Required",
  "Cannot Be Cleaned",
];
const DISPOSITIONS = [
  { id: "restore_onsite",       label: "Restore On-Site" },
  { id: "restore_offsite",      label: "Restore Off-Site" },
  { id: "non_restorable_claim", label: "Non-Restorable — Claim" },
  { id: "homeowner_keeps",      label: "Homeowner Elects to Keep" },
  { id: "trash",                label: "Trash / Disposal" },
  { id: "pending_adjuster",     label: "Uncertain — Pending Adjuster Review" },
];
const RESTORATION_STATUSES = [
  { id: "not_started",          label: "Not Started" },
  { id: "in_cleaning",          label: "In Cleaning" },
  { id: "in_restoration",       label: "In Restoration" },
  { id: "restoration_complete", label: "Restoration Complete" },
  { id: "returned",             label: "Returned to Owner" },
  { id: "disposed",             label: "Disposed / Total Loss" },
];

// ── helpers ────────────────────────────────────────────────────────────────

const useGPS = () => {
  const ref = useRef({ lat: null, lng: null });
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => { ref.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; },
      () => {},
      { enableHighAccuracy: true, timeout: 4000 },
    );
  }, []);
  return ref;
};

const fileToBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = e => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ── Camera trigger (uses native camera via input capture) ──────────────────

const CameraButton = ({ onCapture, label = "Take Photo", testId, accept = "image/*", capture = "environment" }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} capture={capture}
             onChange={async e => {
               const f = e.target.files?.[0];
               if (!f) return;
               const b64 = await fileToBase64(f);
               onCapture(b64);
               e.target.value = "";
             }}
             className="hidden" data-testid={`${testId}-input`} />
      <Button onClick={() => (capture ? openCamera(ref) : ref.current?.click())}
              className="bg-red-600 hover:bg-red-700 text-white text-sm"
              data-testid={testId}>
        <Camera className="w-4 h-4 mr-1.5" /> {label}
      </Button>
    </>
  );
};

// ── Phase 1: Activation ────────────────────────────────────────────────────

const ActivationGate = ({ onActivated, requestId, requestLabel }) => {
  const [open, setOpen] = useState(false);
  const [pin, setPin]   = useState("");
  const [busy, setBusy] = useState(false);
  const gps = useGPS();

  const activate = async () => {
    if (!pin) return toast.error("Enter your PIN");
    setBusy(true);
    try {
      const { data } = await API.post("/contents/activate", {
        request_id: requestId, pin,
        gps_lat: gps.current.lat, gps_lng: gps.current.lng,
        device_id: navigator.userAgent.slice(0, 60),
      });
      toast.success("ContentGuard™ Active");
      onActivated(data.session);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold"
              data-testid="contentguard-open-btn">
        <ShieldCheck className="w-5 h-5 mr-2" /> ContentGuard™ — Begin
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="contentguard-activation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> ContentGuard™ Activation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              Once you begin ContentGuard™, <strong>all steps must be completed in order</strong>.
              Do not move or pack any items until <strong>Area Documentation (Phase 2)</strong> is fully complete.
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                Confirm your identity — enter PIN
              </label>
              <Input type="password" inputMode="numeric"
                     value={pin} onChange={e => setPin(e.target.value)}
                     placeholder="4-8 digit PIN"
                     className="mt-1 text-center text-lg tracking-widest font-bold"
                     data-testid="contentguard-pin" />
              <p className="text-[11px] text-zinc-500 mt-1">{requestLabel}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1"
                      data-testid="contentguard-cancel">Cancel</Button>
              <Button onClick={activate} disabled={busy}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="contentguard-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Begin"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Phase 2: Area + Panoramic ──────────────────────────────────────────────

const AreaPanel = ({ area, requestId, onChanged, onEnterInventory }) => {
  const gps = useGPS();
  const [busy, setBusy] = useState(false);
  const panos = area.panoramics || {};
  const have = PANO_LABELS.filter(p => panos[p.id]);
  const remaining = PANO_LABELS.filter(p => !panos[p.id]);
  const isComplete = area.status === "panoramics_complete" || area.status === "panoramics_overridden";

  const uploadPano = async (label, b64) => {
    setBusy(true);
    try {
      await API.post(`/contents/areas/${area.id}/panoramic`, {
        label, image_base64: b64,
        gps_lat: gps.current.lat, gps_lng: gps.current.lng,
      });
      toast.success(`${label} panoramic confirmed`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const requestOverride = async () => {
    const pin = prompt("Supervisor PIN to override panoramic lock:");
    if (!pin) return;
    const reason = prompt("Reason for override:");
    if (!reason) return;
    try {
      await API.post(`/contents/areas/${area.id}/override`, { pin, reason });
      toast.success("Override approved & logged");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    }
  };

  return (
    <div className="border border-zinc-200 rounded-xl p-3 bg-white" data-testid={`area-${area.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-bold text-zinc-900">{area.name}</p>
          <p className="text-[10px] text-zinc-500 font-mono">{area.area_id}</p>
        </div>
        {isComplete ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-[10px]">
            <Unlock className="w-3 h-3 mr-1" /> Panoramics Complete
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 border text-[10px]">
            <Lock className="w-3 h-3 mr-1" /> {have.length}/5 Panoramics
          </Badge>
        )}
      </div>

      {!isComplete && (
        <>
          {remaining.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-2 text-xs text-amber-800">
              <strong>Next panoramic:</strong> {remaining[0].prompt}
            </div>
          )}
          <div className="grid grid-cols-5 gap-1 mb-2">
            {PANO_LABELS.map(p => (
              <div key={p.id} className={`aspect-square rounded text-[9px] flex flex-col items-center justify-center font-bold border ${
                panos[p.id] ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                            : "bg-zinc-50 border-zinc-200 text-zinc-400"
              }`} data-testid={`pano-cell-${p.id}`}>
                {panos[p.id] ? <CheckCircle2 className="w-3 h-3 mb-0.5" /> : <Camera className="w-3 h-3 mb-0.5" />}
                {p.id}
              </div>
            ))}
          </div>
          {remaining.length > 0 && (
            <CameraButton testId={`pano-capture-${remaining[0].id}`}
                          label={`Capture ${remaining[0].id}`}
                          onCapture={b64 => uploadPano(remaining[0].id, b64)} />
          )}
          <div className="mt-2 text-center">
            <button onClick={requestOverride}
                    className="text-[10px] text-zinc-500 hover:text-red-600 underline"
                    data-testid={`override-${area.id}`}>
              Supervisor override
            </button>
          </div>
        </>
      )}

      {isComplete && (
        <Button onClick={() => onEnterInventory(area)} disabled={busy}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-sm mt-1"
                data-testid={`enter-inventory-${area.id}`}>
          <Camera className="w-4 h-4 mr-1.5" /> Begin Item Inventory
        </Button>
      )}
    </div>
  );
};

// ── Phase 3: Item capture flow (inside a single area) ──────────────────────

const BLANK_ITEM = { full_description: "", brand: "", model: "", color: "", material: "", condition_notes: "", confidence_score: 1 };

const ItemCapture = ({ area, requestId, onDone, onItemAdded, photoCount }) => {
  const { aiOn } = useAI();
  const gps = useGPS();
  const [step, setStep] = useState("category");
  const [category, setCategory] = useState("");
  const [photos, setPhotos] = useState([]);  // [{image_base64,type}]
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiOriginal, setAiOriginal] = useState(null);
  const [edited, setEdited] = useState(false);
  const [item, setItem] = useState(null);  // saved item once created
  const [barcode, setBarcode] = useState(null);

  const highValue = HIGH_VALUE_CATS.has(category);
  const overLimit = photoCount >= 500;
  const nearLimit = photoCount >= 450;

  const capture = b64 => {
    if (overLimit) return toast.error("Photo limit reached (500). Supervisor override required.");
    const type = photos.length === 0 ? "main" : "angle";
    setPhotos([...photos, { image_base64: b64, type, pre_existing: false,
                            timestamp_utc: new Date().toISOString(),
                            gps_lat: gps.current.lat, gps_lng: gps.current.lng }]);
    if (photos.length === 0 && aiOn) runAI(b64);
  };

  const runAI = async img => {
    setAiBusy(true);
    try {
      const { data } = await API.post("/contents/ai-describe", {
        image_base64: img, category,
      });
      setAi(data.ai_description);
      setAiOriginal(data.ai_description);
      setStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI description failed");
      setStep("review");
      setAi({ full_description: "", brand: "", model: "", color: "", material: "",
              condition_notes: "", confidence_score: 0 });
    } finally { setAiBusy(false); }
  };

  const updateAi = (k, v) => {
    setAi(a => ({ ...a, [k]: v }));
    if (JSON.stringify({ ...ai, [k]: v }) !== JSON.stringify(aiOriginal)) setEdited(true);
  };

  const saveItem = async () => {
    try {
      const { data } = await API.post(`/contents/${requestId}/items`, {
        area_id: area.id, category, barcode: barcode?.value || null,
        barcode_type: barcode?.type || null,
        barcode_product_details: barcode?.details || null,
        photos, ai_description: ai,
      });
      setItem(data.item);
      if (edited && aiOn) {
        await API.patch(`/contents/items/${data.item.item_id}`, {
          ai_description: ai, ai_edited: true,
        });
      }
      setStep("assessment");
      onItemAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const finishItem = () => {
    setStep("category");
    setCategory("");
    setPhotos([]);
    setAi(null); setAiOriginal(null); setEdited(false);
    setItem(null);
    setBarcode(null);
  };

  // ── Category picker ─────
  if (step === "category") return (
    <div className="space-y-3" data-testid="item-step-category">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase text-zinc-500">Item — Select Category</p>
        <span className={`text-[11px] font-bold ${overLimit ? "text-red-600" : nearLimit ? "text-amber-600" : "text-zinc-500"}`}
              data-testid="photo-counter">Item Photos: {photoCount} / 500</span>
      </div>
      {overLimit && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
        Photo limit reached. Supervisor override required to continue.</div>}
      {nearLimit && !overLimit && <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
        Approaching 500 photo limit ({photoCount} so far).</div>}
      <div className="grid grid-cols-2 gap-1.5">
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => { setCategory(c); setStep("photo"); }}
                  className={`text-left text-xs px-2.5 py-2 rounded-lg border ${
                    HIGH_VALUE_CATS.has(c)
                      ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100"
                      : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
                  data-testid={`cat-${c.replace(/[^a-zA-Z]/g, '')}`}>
            {HIGH_VALUE_CATS.has(c) && <Star className="w-3 h-3 inline mr-1 text-amber-500 fill-amber-500" />}
            {c}
          </button>
        ))}
      </div>
      <Button variant="outline" onClick={onDone} className="w-full text-sm"
              data-testid="item-back-to-areas">
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to Areas
      </Button>
    </div>
  );

  // ── Photo step ─────
  if (step === "photo") return (
    <div className="space-y-3" data-testid="item-step-photo">
      <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-xs text-zinc-700 space-y-1">
        <p>📷 Center the item in frame. Include any visible damage, brand markings, or serial numbers.</p>
        <p>↔️ Step back enough to show the full item.</p>
        <p>⚠️ If item has pre-existing damage, take a close-up <strong>after</strong> confirming the main photo.</p>
      </div>
      {highValue && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
          <Star className="w-4 h-4 inline mr-1 text-amber-500 fill-amber-500" />
          High-value item. Photograph all serial numbers, hallmarks, certificates or appraisal tags.
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square rounded overflow-hidden border border-zinc-200">
            <img src={p.image_base64} alt="" className="w-full h-full object-cover" />
            <Badge className="absolute top-1 left-1 text-[9px]">{p.type}</Badge>
          </div>
        ))}
      </div>
      {aiBusy && <div className="flex items-center justify-center gap-2 text-xs text-zinc-600 py-2">
        <Loader2 className="w-4 h-4 animate-spin" /> AI describing item…</div>}
      <div className="grid grid-cols-2 gap-2">
        <CameraButton testId="item-photo-capture" label={photos.length === 0 ? "Main Photo" : "+ Add Angle"} onCapture={capture} />
        {aiOn ? (
          <Button variant="outline" disabled={photos.length === 0 || aiBusy}
                  onClick={() => runAI(photos[0].image_base64)}
                  data-testid="rerun-ai-btn">
            <Sparkles className="w-4 h-4 mr-1" /> AI Describe
          </Button>
        ) : (
          <Button variant="outline" disabled={photos.length === 0}
                  onClick={() => { setAi(BLANK_ITEM); setAiOriginal(BLANK_ITEM); setStep("review"); }}
                  data-testid="describe-item-btn">
            Describe Item
          </Button>
        )}
      </div>
    </div>
  );

  // ── AI Review ─────
  if (step === "review") return (
    <div className="space-y-3" data-testid="item-step-review">
      <p className="text-xs font-bold uppercase text-zinc-500">{aiOn ? "AI Description" : "Item Description"}</p>
      {aiOn && aiOriginal?.full_description && <AIResultNote feature="contents" text={aiOriginal.full_description} />}
      {ai.confidence_score < 0.65 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
          Low confidence ({(ai.confidence_score * 100).toFixed(0)}%) — review carefully or retake.
        </div>
      )}
      <Textarea value={ai.full_description || ""} onChange={e => updateAi("full_description", e.target.value)}
                rows={3} className="text-sm" data-testid="ai-full-description" />
      <div className="grid grid-cols-2 gap-2">
        {["brand", "model", "color", "material"].map(k => (
          <Input key={k} placeholder={k} value={ai[k] || ""}
                 onChange={e => updateAi(k, e.target.value)}
                 className="text-xs" data-testid={`ai-${k}`} />
        ))}
      </div>
      <Textarea placeholder="condition notes" value={ai.condition_notes || ""}
                onChange={e => updateAi("condition_notes", e.target.value)}
                rows={2} className="text-xs" data-testid="ai-notes" />
      {edited && aiOn && <p className="text-[10px] text-zinc-500 italic">Manually edited — will be flagged in audit log.</p>}
      <div className="flex gap-2">
        <Button onClick={saveItem} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="approve-ai-btn">
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
        </Button>
        <Button variant="outline" onClick={() => setStep("photo")}
                data-testid="back-to-photo-btn">
          <ChevronLeft className="w-4 h-4" /> Retake
        </Button>
      </div>
    </div>
  );

  // ── Assessment ─────
  if (step === "assessment") {
    const updateItem = async patch => {
      try {
        const { data } = await API.patch(`/contents/items/${item.item_id}`, patch);
        setItem(data.item);
      } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    };
    const toggleClean = c => {
      const cur = item.cleaning_requirements || [];
      const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
      updateItem({ cleaning_requirements: next });
    };
    const setDisposition = id => {
      const update = { disposition: id };
      updateItem(update);
    };
    const suggested = item.condition === "destroyed" ? "non_restorable_claim"
                    : (item.condition === "like_new" || item.condition === "good") ? "restore_onsite"
                    : null;
    return (
      <div className="space-y-3" data-testid="item-step-assessment">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase text-zinc-500">Damage Assessment</p>
          {item.high_value_flag && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 border text-[10px]">
              <Star className="w-3 h-3 mr-1 fill-amber-500" /> High-Value
            </Badge>
          )}
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Condition</p>
          <div className="grid grid-cols-2 gap-1.5">
            {CONDITIONS.map(c => (
              <button key={c.id} onClick={() => updateItem({ condition: c.id })}
                      className={`text-[11px] px-2 py-1.5 rounded border ${
                        item.condition === c.id ? c.color : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                      }`} data-testid={`cond-${c.id}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {(item.condition === "damaged" || item.condition === "destroyed") && (
          <div>
            <p className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Damage Severity</p>
            <div className="grid grid-cols-2 gap-1.5">
              {SEVERITIES.map(s => (
                <button key={s.id} onClick={() => updateItem({ damage_severity: s.id })}
                        className={`text-[11px] px-2 py-1.5 rounded border ${
                          item.damage_severity === s.id ? "bg-red-100 border-red-300 text-red-700"
                                                        : "bg-white border-zinc-200 text-zinc-600"
                        }`} data-testid={`sev-${s.id}`}>{s.label}</button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Cleaning Requirements</p>
          <div className="grid grid-cols-2 gap-1">
            {CLEANINGS.map(c => (
              <label key={c} className="text-[11px] flex items-center gap-1.5 px-2 py-1.5 rounded border border-zinc-200 cursor-pointer hover:bg-zinc-50">
                <input type="checkbox"
                       checked={(item.cleaning_requirements || []).includes(c)}
                       onChange={() => toggleClean(c)}
                       data-testid={`clean-${c.replace(/[^a-zA-Z]/g, '')}`} />
                {c}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase text-zinc-500 mb-1">
            Disposition {suggested && <span className="text-amber-600">— suggested: {DISPOSITIONS.find(d => d.id === suggested)?.label}</span>}
          </p>
          <div className="grid grid-cols-1 gap-1">
            {DISPOSITIONS.map(d => (
              <button key={d.id} onClick={() => setDisposition(d.id)}
                      className={`text-left text-[11px] px-2.5 py-1.5 rounded border ${
                        item.disposition === d.id ? "bg-zinc-900 text-white border-zinc-900"
                                                  : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                      }`} data-testid={`disp-${d.id}`}>{d.label}</button>
            ))}
          </div>
        </div>

        {item.disposition === "non_restorable_claim" && item.rcv_estimated_value !== null && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800">
            RCV ${item.rcv_estimated_value} · ACV ${item.acv_estimated_value} · Dep {item.depreciation_percentage}%
            {item.replacement_recommendation_url && <a href={item.replacement_recommendation_url} target="_blank" rel="noreferrer" className="block text-emerald-700 underline mt-1">View replacement match →</a>}
          </div>
        )}
        {item.disposition === "trash" && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            Disposal flagged — homeowner must approve via ARM portal before disposal proceeds.
          </div>
        )}

        <Button onClick={finishItem} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="finish-item-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Save & Add Next Item
        </Button>
      </div>
    );
  }
};

// ── Phase 4: Boxes ─────────────────────────────────────────────────────────

const BoxPanel = ({ requestId, areas, items, onRefresh }) => {
  const gps = useGPS();
  const [boxes, setBoxes] = useState([]);
  const [areaSel, setAreaSel] = useState("");
  const [count, setCount] = useState(1);

  const load = useCallback(async () => {
    const { data } = await API.get(`/contents/${requestId}/boxes`);
    setBoxes(data.boxes);
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const createBoxes = async () => {
    if (!areaSel) return toast.error("Pick an area");
    try {
      await API.post(`/contents/${requestId}/boxes`, { area_id: areaSel, count: Number(count) || 1 });
      toast.success(`${count} box label(s) created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const assign = async (boxDbId, itemId) => {
    try { await API.post(`/contents/boxes/${boxDbId}/assign`, { item_id: itemId }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const seal = async boxDbId => {
    const input = window.prompt("Enter any string to confirm seal photo captured (in production use camera):");
    if (!input) return;
    try {
      await API.post(`/contents/boxes/${boxDbId}/seal`, {
        seal_photo_base64: input,
        gps_lat: gps.current.lat, gps_lng: gps.current.lng,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seal failed"); }
  };

  const loadForTransport = async boxDbId => {
    const vehicle_id = prompt("Vehicle ID / license plate:"); if (!vehicle_id) return;
    const driver_name = prompt("Driver name:"); if (!driver_name) return;
    const destination = prompt("Destination (Warehouse / Storage / Other):"); if (!destination) return;
    try {
      await API.post(`/contents/boxes/${boxDbId}/transport`, {
        vehicle_id, driver_name, destination,
        gps_lat: gps.current.lat, gps_lng: gps.current.lng,
      });
      toast.success("Loaded for transport");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const receive = async boxDbId => {
    const facility_name = prompt("Facility name:");
    if (!facility_name) return;
    try {
      await API.post(`/contents/boxes/${boxDbId}/receive`, {
        facility_id: facility_name.toLowerCase().replace(/\s+/g, "-"),
        facility_name,
        gps_lat: gps.current.lat, gps_lng: gps.current.lng,
      });
      toast.success("Received at facility");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="boxes-panel">
      <div className="flex gap-2">
        <select value={areaSel} onChange={e => setAreaSel(e.target.value)}
                className="flex-1 text-xs border border-zinc-300 rounded px-2"
                data-testid="box-area-select">
          <option value="">Select area…</option>
          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <Input type="number" min={1} value={count} onChange={e => setCount(e.target.value)}
               className="w-16 text-xs" data-testid="box-count" />
        <Button onClick={createBoxes} className="bg-red-600 hover:bg-red-700 text-white text-xs"
                data-testid="box-create-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Create Labels
        </Button>
      </div>

      {boxes.length === 0 && <p className="text-xs text-zinc-400 italic">No boxes yet.</p>}
      {boxes.map(b => (
        <div key={b.id} className="border border-zinc-200 rounded p-2.5 bg-white space-y-2"
             data-testid={`box-${b.id}`}>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs font-bold text-zinc-900">{b.label}</p>
              <p className="text-[10px] text-zinc-500 font-mono">{b.box_id}</p>
            </div>
            <Badge className="text-[9px]">{b.status}</Badge>
          </div>
          <p className="text-[11px] text-zinc-600">{b.items?.length || 0} item(s) assigned</p>
          {b.status === "open" && (
            <>
              <select onChange={e => e.target.value && assign(b.id, e.target.value)}
                      className="w-full text-xs border border-zinc-200 rounded px-2 py-1"
                      data-testid={`assign-${b.id}`}>
                <option value="">+ Add item to box…</option>
                {items.filter(i => !i.box_id).filter(i => i.area_db_id === b.area_db_id).map(i => (
                  <option key={i.item_id} value={i.item_id}>{i.ai_description?.full_description?.slice(0, 60) || i.category}</option>
                ))}
              </select>
              <Button onClick={() => seal(b.id)} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-xs"
                      data-testid={`seal-${b.id}`} disabled={!b.items?.length}>
                <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Seal Box
              </Button>
            </>
          )}
          {b.status === "sealed" && (
            <Button onClick={() => loadForTransport(b.id)} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">
              <Truck className="w-3.5 h-3.5 mr-1" /> Mark Loaded for Transport
            </Button>
          )}
          {b.status === "in_transit" && (
            <Button onClick={() => receive(b.id)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
              <Warehouse className="w-3.5 h-3.5 mr-1" /> Confirm Received at Facility
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Phase 5: Reports & signoff ─────────────────────────────────────────────

const ReportsPanel = ({ requestId, onRefresh }) => {
  const [summary, setSummary] = useState(null);
  const [coc, setCoc] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        API.get(`/contents/${requestId}/reports/summary`),
        API.get(`/contents/${requestId}/coc`),
      ]);
      setSummary(s.data);
      setCoc(c.data.events);
    } catch (e) { /* silent */ }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const signoff = async () => {
    const name = prompt("Homeowner name to record:");
    if (!name) return;
    try {
      await API.post(`/contents/${requestId}/signoff`, {
        signature_base64: `signed-by:${name}`,
      });
      toast.success("Sign-off captured");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!summary) return null;
  const t = summary.totals;

  return (
    <div className="space-y-3" data-testid="reports-panel">
      <div className="grid grid-cols-3 gap-2 text-center">
        {[
          ["Areas", t.areas], ["Items", t.items], ["Boxes", t.boxes],
          ["★ High-Value", t.high_value_items],
          ["Non-Restorable", t.non_restorable_items],
          ["ACV $", t.acv_total.toFixed(0)],
        ].map(([l, v]) => (
          <div key={l} className="bg-zinc-50 rounded p-2 border border-zinc-200">
            <p className="text-[9px] uppercase text-zinc-500 font-bold">{l}</p>
            <p className="text-lg font-bold text-zinc-900">{v}</p>
          </div>
        ))}
      </div>

      {summary.report_blocked_reason ? (
        <div className="bg-red-50 border border-red-200 rounded p-2.5 text-xs text-red-800"
             data-testid="reports-blocked">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          {summary.report_blocked_reason}
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-xs text-emerald-800">
          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Sign-off captured. Reports ready.
        </div>
      )}

      <Button onClick={signoff} className="w-full bg-red-600 hover:bg-red-700 text-white text-sm"
              data-testid="signoff-btn">
        <Send className="w-4 h-4 mr-1.5" /> Capture Homeowner Sign-Off
      </Button>

      <div>
        <p className="text-[10px] font-bold uppercase text-zinc-500 mb-1">Chain of Custody — {coc.length} events</p>
        <div className="max-h-48 overflow-y-auto bg-zinc-50 rounded border border-zinc-200 text-[10px] font-mono"
             data-testid="coc-log">
          {coc.slice(-20).reverse().map(e => (
            <div key={e.id} className="px-2 py-1 border-b border-zinc-100">
              <span className="text-zinc-500">{e.timestamp_utc?.slice(11, 19)}</span>{" "}
              <span className="font-bold text-zinc-700">{e.event_type}</span>
              {e.notes && <span className="text-zinc-500"> — {e.notes.slice(0, 60)}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Master component ───────────────────────────────────────────────────────

export default function ContentGuard({ requestId, requestLabel = "" }) {
  const [open, setOpen]       = useState(false);
  const [session, setSession] = useState(null);
  const [areas, setAreas]     = useState([]);
  const [items, setItems]     = useState([]);
  const [activeArea, setActiveArea] = useState(null);
  const [phase, setPhase]     = useState("areas");  // areas | inventory | boxes | reports
  const [photoTotal, setPhotoTotal] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [s, it, pc] = await Promise.all([
        API.get(`/contents/session/${requestId}`),
        API.get(`/contents/${requestId}/items`),
        API.get(`/contents/${requestId}/photo-count`),
      ]);
      setSession(s.data.session);
      setAreas(s.data.areas || []);
      setItems(it.data.items || []);
      setPhotoTotal(pc.data.total || 0);
    } catch (e) { /* silent */ }
  }, [requestId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const activated = sess => { setSession(sess); refresh(); };

  const addArea = async () => {
    const name = prompt("Area name (or pick from list):\n" + AREA_PRESETS.join(", "));
    if (!name) return;
    try {
      await API.post(`/contents/${requestId}/areas`, { name });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="pt-2 border-t border-zinc-100" data-testid="contentguard-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-red-500" /> Contents (ContentGuard™)
        </p>
        <button onClick={() => setOpen(!open)} className="text-xs text-zinc-500 underline"
                data-testid="contentguard-toggle">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && !session && (
        <ActivationGate onActivated={activated} requestId={requestId} requestLabel={requestLabel} />
      )}

      {open && session && (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <div className="flex-1">
              <p className="font-bold">ContentGuard Active — {session.tech_name}</p>
              <p className="text-[10px] text-emerald-600">{new Date(session.opened_at).toLocaleString()} · {session.address}</p>
            </div>
          </div>

          <div className="grid grid-cols-4 text-[10px] font-bold gap-1 border border-zinc-200 rounded overflow-hidden">
            {[
              ["areas",     "Areas"],
              ["inventory", "Items"],
              ["boxes",     "Boxes"],
              ["reports",   "Reports"],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setPhase(id)}
                      className={`py-2 ${phase === id ? "bg-red-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                      data-testid={`cg-tab-${id}`}>{label}</button>
            ))}
          </div>

          {phase === "areas" && (
            <div className="space-y-2">
              {areas.map(a => (
                <AreaPanel key={a.id} area={a} requestId={requestId}
                           onChanged={refresh}
                           onEnterInventory={ar => { setActiveArea(ar); setPhase("inventory"); }} />
              ))}
              <Button onClick={addArea} variant="outline" className="w-full text-sm"
                      data-testid="add-area-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Area
              </Button>
            </div>
          )}

          {phase === "inventory" && (
            <div className="space-y-2">
              {!activeArea && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Select an area to inventory:</p>
                  {areas.filter(a => a.status === "panoramics_complete" || a.status === "panoramics_overridden").map(a => (
                    <button key={a.id} onClick={() => setActiveArea(a)}
                            className="w-full text-left px-3 py-2 bg-white border border-zinc-200 rounded hover:bg-zinc-50 text-sm"
                            data-testid={`pick-area-${a.id}`}>
                      <span className="font-bold">{a.name}</span>{" "}
                      <span className="text-xs text-zinc-500">— {items.filter(i => i.area_db_id === a.id).length} items</span>
                    </button>
                  ))}
                  {areas.filter(a => a.status === "panoramics_complete" || a.status === "panoramics_overridden").length === 0 && (
                    <p className="text-xs text-amber-700 italic">No areas with completed panoramics yet.</p>
                  )}
                </div>
              )}
              {activeArea && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">{activeArea.name}</span>
                    <button onClick={() => setActiveArea(null)} className="text-zinc-500 underline"
                            data-testid="change-area-btn">change area</button>
                  </div>
                  <ItemCapture area={activeArea} requestId={requestId}
                               photoCount={photoTotal}
                               onItemAdded={refresh}
                               onDone={() => setActiveArea(null)} />
                </>
              )}
            </div>
          )}

          {phase === "boxes" && (
            <BoxPanel requestId={requestId} areas={areas} items={items} onRefresh={refresh} />
          )}

          {phase === "reports" && (
            <ReportsPanel requestId={requestId} onRefresh={refresh} />
          )}
        </div>
      )}
    </div>
  );
}
